/**
 * Use free gift module to display free gift associated with a SKU
 *
 * @module views/components/FreeGift
 * @memberof -Common
 */
import './FreeGift.scss';
import 'swiper/swiper.min.css';
import 'swiper/swiper-bundle.min.css';

import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import SwiperCore, { A11y, Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import palette_variables from 'web-styles/src/_exports.module.scss';

import Button from '@ulta/core/components/Button/Button';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import { handleInactiveSwiperSlides } from '@ulta/utils/carouselUtils/carouselUtils';
import { constants } from '@ulta/utils/constants/constants';

import GiftItem from '../GiftItem/GiftItem';

SwiperCore.use( [Navigation, Keyboard, A11y] );

let swiperCounter = 1;

/**
  * Represents a FreeGift component
  * @method
  * @param {FreeGiftProps} props - React properties passed from composition
  * @returns FreeGift
  */
export const FreeGift = function( props ){
  const freeGiftRef = useRef( null );
  const {
    backgroundColor,
    title,
    gifts,
    id,
    previousAccessibility,
    nextAccessibility,
    nextClickAction,
    previousClickAction,
    carouselAccessibility,
    railAccessibility = defaultProps.railAccessibility,
    rootMargin = defaultProps.rootMargin,
    root = defaultProps.root,
    threshold = defaultProps.threshold
  } = props;

  const [currentIndex, updateCurrentIndex] = useState( 1 );
  swiperCounter += 1;
  const isSingleItem = gifts?.length === 1;
  const isLastItem =  currentIndex === gifts?.length;

  useIntersectionObserver( freeGiftRef, {
    root,
    rootMargin,
    threshold
  }, handleIntersection( props ) );

  return (
    <div className='FreeGift'
      id={ id }
      ref={ freeGiftRef }
    >
      <GridContainer>
        { title &&
          <div className='FreeGift__mobileTitle'>
            <Text
              htmlTag={ title.htmlTag }
              textStyle={ title.textStyle }
              color={ title.color }
              textAlign={ title.textAlign }
            >
              { title.text }
            </Text>
          </div>
        }
        <div className={
          classNames( 'FreeGift__container', {
            'FreeGift__container--singleItem': isSingleItem,
            'FreeGift__container--lastItem': isLastItem && !isSingleItem,
            'FreeGift__container--firstItem': !isSingleItem
          } )
        }
        role='region'
        aria-label={ railAccessibility }
        >
          { carouselAccessibility &&
            <p className='sr-only'>{ carouselAccessibility }</p>
          }
          { gifts?.length >= 2 &&
            <div className={ classNames( `FreeGift__carouselPagination FreeGift__carouselPagination-${swiperCounter}`, {
              'FreeGift__carouselPagination--lastItem': isLastItem
            } ) }
            >
              <Button
                variant='navigation'
                iconImage='CaretBack'
                iconSize='lg'
                className='FreeGift__carouselPagination__button FreeGift__carouselPagination__button--back'
                ariaLabel={ previousAccessibility }
                ariaHiddenIcon={ true }
                action={ previousClickAction }
              />
              <span className='FreeGift__carouselPagination--compact FreeGift__carouselPagination__progress'>
                { currentIndex }/{ gifts.length }
              </span>
              <Button
                variant='navigation'
                iconImage='CaretForward'
                iconSize='lg'
                className='FreeGift__carouselPagination__button FreeGift__carouselPagination__button--forward'
                ariaLabel={ nextAccessibility }
                ariaHiddenIcon={ true }
                action={ nextClickAction }
              />
            </div>
          }
          <Swiper
            onSwiper={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
            onSlideChange={ ( swiper ) => {
              updateCurrentIndex( swiper?.realIndex + 1 );
              handleInactiveSwiperSlides( swiper );
            } }
            watchOverflow={ true }
            watchSlidesProgress={ true }
            watchSlidesVisibility={ true }
            centeredSlides={ true }
            freeMode={ false }
            speed={ 600 }
            preloadImages={ true }
            spaceBetween={ isSingleItem ? 0 : 16 }
            navigation={ {
              prevEl: `.FreeGift__carouselPagination-${swiperCounter} .FreeGift__carouselPagination__button--back`,
              nextEl: `.FreeGift__carouselPagination-${swiperCounter} .FreeGift__carouselPagination__button--forward`,
              disabledClass: 'FreeGift__carouselPagination__button--disabled',
              hiddenClass: 'FreeGift__carouselPagination__button--hidden'
            } }
            breakpoints={ {
              [palette_variables.breakpointLG]: {
                slidesPerView:isSingleItem ? 1 : 1.2,
                slidesPerGroup: 1
              },
              [palette_variables.breakpointMD]: {
                slidesPerView: isSingleItem ? 1 : 1.5,
                slidesPerGroup: 1
              },
              [palette_variables.breakpointSM]: {
                slidesPerView: isSingleItem ? 1 : 1.35,
                slidesPerGroup: 1
              }
            } }
          >
            { gifts?.length > 0 && gifts.map( ( product, giftIndex ) => (
              <SwiperSlide key={ `FreeGift${ giftIndex + 1 } ` }>
                <div
                  className={
                    classNames( 'FreeGift__itemclass', {
                      ...( backgroundColor && { [`FreeGift__backgroundColor--${backgroundColor}`]: backgroundColor } ),
                      'FreeGift__itemclass--lastItem': !isSingleItem && isLastItem,
                      'FreeGift__itemclass--singleItem': isSingleItem,
                      'FreeGift__itemclass--firstItem': currentIndex === 1
                    } )
                  }
                >
                  <GiftItem
                    key={ giftIndex }
                    title={ title }
                    image={ product.image }
                    eyebrow={ product.eyebrow }
                    description={ product.description }
                    linkAction={ product.linkAction }
                  />
                </div>
              </SwiperSlide>
            ) ) }
          </Swiper>
        </div>
      </GridContainer>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef FreeGiftProps
 * @type {object}
 * @property {array} gifts - The default value for gifts is set to empty array.
 * @property {object} title - The object that contains the title.
 * @property {string} backgroundColor - Sets the title for productRail list.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {string} carouselAccessibility - Set the value for aria label for carousel
 * @property {string} railAccessibility - Set the value for aria label for product rail
 * @property {object} nextClickAction - Action for when the next slide button is pressed
 * @property {object} previousClickAction - Action for when the previous slide button is pressed
 * @property {string} id - Sets the id value
 */
export const propTypes = {
  /** The prop that holds backgroundColor for the carousel.*/
  backgroundColor: PropTypes.string,
  /** The prop that holds title for the carousel.*/
  title: PropTypes.object,
  /** The props that the array of gifts */
  gifts: PropTypes.array,
  /** The prop that holds the aria label for previous button. */
  previousAccessibility: PropTypes.string,
  /** The prop that holds the aria label for next button. */
  nextAccessibility: PropTypes.string,
  /** Sets the margin bottom spacer value */
  carouselAccessibility: PropTypes.string,
  /** Set the value for aria label for carousel */
  railAccessibility: PropTypes.string,
  /** Set the value for aria label for product rail */
  nextClickAction: PropTypes.object,
  /** Action for when the next slide button is pressed */
  previousClickAction: PropTypes.object,
  /** Sets the id value */
  id: PropTypes.string

};

/**
 * Default values for passed properties
 * @type {object}
 * @property {bool} railAccessibility='carousel' - The default value for aria-label is set to text 'carousel'.
 */

export const defaultProps = {
  railAccessibility: 'carousel',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

FreeGift.propTypes = propTypes;
FreeGift.defaultProps = defaultProps;
export default FreeGift;